// Synergies (PvP depth, layer 2: mythic-idle-design/synergies.md): bonuses a PvP line-up gets from what its creatures
// share. Pure and deterministic, shared by the server, the client (chips, practice, replays) and the bots.
//
// - Region: 2 / 4 creatures from one region give each of that region's creatures its perk: Midgard HP, Hellas damage,
//   Yomi Speed. An Awakened form counts as its base's region. Different regions stack (2 Midgard + 2 Hellas: both).
// - Element: 2 / 3 creatures sharing an element give each of them more damage with that element's moves. A
//   dual-element creature counts for both of its elements.
// - Kin: two or more different forms of one line (a base and its Awakened form, or two stages) each get a little HP.
//
// Counted on the line-up as it fights: one of each species (shared/pvp.ts oneOfEach), so a second copy adds nothing.
// Applied before the fight (battle(), shared/arena.ts): HP and Speed on the stats, damage as a multiplier in the blow
// formula (shared/combat.ts blowBase). Power (shared/power.ts) leaves synergies out, like type match-ups. On the board
// the numbers below are scaled by perk (shared/board.ts BoardRules.synergyScale, SynergyOpts.scale), and the chips
// show the scaled numbers: what the fight applies.
//
// Where: the Arena and ranked, Element Halls, friendly duels, training, practice runs and tournaments. Never Guild War,
// hunts or bosses. An admin switch (kv 'synergy-on', default on) turns them off everywhere at once; off, every battle
// is exactly as before synergies existed. Tournament games record whether they had them (shared/tournament.ts
// BATTLE_RULES 4 with, 3 without), so a replay always rebuilds the game as it was played.
import { ELEMENTS, type Element } from './elements.ts';
import { LINES, SPECIES, type RegionId } from './species.ts';
import type { Fighter } from './combat.ts';

/**
 * Live switch, mirrored from the server (kv 'synergy-on'): the server sets it at start and when an admin flips it,
 * the client from 'hello' and the 'synergy' message. battle() reads it when no explicit choice is passed.
 */
export const SYNERGY_SWITCH = { on: true };

export type RegionPerk = 'hp' | 'dmg' | 'swift';

/**
 * The numbers (season one, 2026-10-15), percentages per tier. Tuned in sim/consistency.ts (synergy rows): the best
 * synergy line-up wins about 75% against the same five without synergies, and a line-up 10% stronger without them still
 * beats it about 85% of the time. These are the fronts-in-order numbers; the board scales them by perk (shared/board.ts
 * BoardRules.synergyScale) to land the same rows at about 78%.
 */
export const SYNERGY = {
  /** Creatures of one region for tier 1 and tier 2. */
  regionAt: [2, 4] as const,
  region: {
    midgard: { perk: 'hp', pct: [3, 5] },
    hellas: { perk: 'dmg', pct: [3, 5] },
    // Speed shortens the beat by less than it grows (and the cooldowns by at most 10%), so Yomi's step is larger.
    yomi: { perk: 'swift', pct: [6, 12] },
  } as Record<RegionId, { perk: RegionPerk; pct: readonly [number, number] }>,
  /** Creatures sharing an element for tier 1 and tier 2, and the damage bonus on that element's moves. */
  elementAt: [2, 3] as const,
  element: [3, 5] as const,
  /** Kin: HP for each form of a line when two or more forms are in the line-up. */
  kinAt: 2,
  kin: 3,
  /**
   * A ranked season's featured region (shared/ranked.ts theme rule, opts.featuredRegion): points added to its perk at
   * each tier. Speed counts double, as in the region numbers above.
   */
  featured: { hp: 5, dmg: 5, swift: 10 } as Record<RegionPerk, number>,
} as const;

/**
 * A scale on the perk percentages, by what a synergy raises ('kin' for kin's HP): absent = ×1. The board's own
 * (shared/board.ts BoardRules.synergyScale) is applied to every board fight by shared/pvpfight.ts; fronts in order
 * never pass one.
 */
export type SynergyScale = Partial<Record<RegionPerk | 'moves' | 'kin', number>>;

export interface SynergyOpts {
  /** A ranked season theme: this region's perk is a bit stronger (SYNERGY.featured). Tournaments never pass it. */
  featuredRegion?: RegionId;
  /** Bench rows only: keep just these synergies, each a kind ('kin') or a kind and key ('region:yomi'). */
  only?: readonly string[];
  /** The fight's scale on the numbers (the board's, SynergyScale); absent: the numbers as listed in SYNERGY. */
  scale?: SynergyScale;
}

export type SynergyKind = 'region' | 'element' | 'kin';

export interface Synergy {
  kind: SynergyKind;
  /** Region id, element id or line id. */
  key: string;
  /** Creatures in the line-up that have it. */
  count: number;
  /** Tier reached: 0 none yet, 1 or 2 (kin has one tier). */
  tier: number;
  /** Count for the first tier not reached yet; null at the top. */
  next: number | null;
  /** Count for the highest tier (the "4" of "2/4"). */
  max: number;
  /** The bonus at this tier in % (0 at tier 0), and at each tier. */
  pct: number;
  tiers: number[];
  /** What it raises: HP, Speed, all damage, or the damage of this element's moves. */
  perk: RegionPerk | 'moves';
  /** Featured this season (region only). */
  featured?: boolean;
  /** Indexes into the line-up of the creatures that have it (those that get the bonus once it is active). */
  who: number[];
}

/** A species' region for synergies: an Awakened form counts as its base's. */
export function synergyRegion(species: string): RegionId | undefined {
  const sp = SPECIES[species];
  if (!sp) return undefined;
  return sp.awakenedFrom ? SPECIES[sp.awakenedFrom]?.region ?? sp.region : sp.region;
}

const lineOf = (species: string) => SPECIES[species]?.line;
const round1 = (x: number) => Math.round(x * 10) / 10;
/** A percentage under a scale, to one decimal: what the chips show is what the fight applies. */
const scaled = (pct: number, k = 1) => (k === 1 ? pct : round1(pct * k));
/** A region's bonus at each tier, in %: featured (a ranked theme) or not, under a scale (the board's). */
export const regionPct = (region: RegionId, featured = false, scale?: SynergyScale): number[] => {
  const def = SYNERGY.region[region];
  return def.pct.map((p) => scaled(round1(featured ? p + SYNERGY.featured[def.perk] : p), scale?.[def.perk]));
};
/** The element bonus at each tier, in %, under a scale. */
export const elementPct = (scale?: SynergyScale): number[] => SYNERGY.element.map((p) => scaled(p, scale?.moves));
/** Kin's HP bonus, in %, under a scale. */
export const kinPct = (scale?: SynergyScale): number => scaled(SYNERGY.kin, scale?.kin);

/**
 * Every region and element present in a line-up (count 1 and up, so a page can show near-misses) and every kin line
 * with two or more forms. Duplicate species count once (the first). Sorted: active first, then by tier and count.
 */
export function synergies(team: readonly { species: string }[], opts: SynergyOpts = {}): Synergy[] {
  const seen = new Set<string>();
  const idx: number[] = [];
  team.forEach((c, i) => { if (SPECIES[c.species] && !seen.has(c.species)) { seen.add(c.species); idx.push(i); } });
  const groups = new Map<string, { kind: SynergyKind; key: string; who: number[] }>();
  const add = (kind: SynergyKind, key: string, i: number) => {
    const id = `${kind}:${key}`;
    const g = groups.get(id) ?? { kind, key, who: [] };
    g.who.push(i);
    groups.set(id, g);
  };
  for (const i of idx) {
    const sp = team[i].species;
    const r = synergyRegion(sp);
    if (r) add('region', r, i);
    for (const e of SPECIES[sp].elements) add('element', e, i);
    add('kin', lineOf(sp)!, i);
  }
  const out: Synergy[] = [];
  for (const g of groups.values()) {
    const n = g.who.length;
    if (g.kind === 'region') {
      const def = SYNERGY.region[g.key as RegionId];
      const featured = opts.featuredRegion === g.key;
      const tiers = regionPct(g.key as RegionId, featured, opts.scale);
      const tier = SYNERGY.regionAt.filter((at) => n >= at).length;
      out.push({ kind: 'region', key: g.key, count: n, tier, next: SYNERGY.regionAt[tier] ?? null, max: SYNERGY.regionAt[1], pct: tier ? tiers[tier - 1] : 0, tiers, perk: def.perk, ...(featured ? { featured } : {}), who: g.who });
    } else if (g.kind === 'element') {
      const tier = SYNERGY.elementAt.filter((at) => n >= at).length;
      const tiers = elementPct(opts.scale);
      out.push({ kind: 'element', key: g.key, count: n, tier, next: SYNERGY.elementAt[tier] ?? null, max: SYNERGY.elementAt[1], pct: tier ? tiers[tier - 1] : 0, tiers, perk: 'moves', who: g.who });
    } else if (n >= SYNERGY.kinAt) {
      const pct = kinPct(opts.scale);
      out.push({ kind: 'kin', key: g.key, count: n, tier: 1, next: null, max: SYNERGY.kinAt, pct, tiers: [pct], perk: 'hp', who: g.who });
    }
  }
  const order: Record<SynergyKind, number> = { region: 0, element: 1, kin: 2 };
  return out.sort((a, b) => b.tier - a.tier || b.count - a.count || order[a.kind] - order[b.kind] || a.key.localeCompare(b.key));
}

/** Active synergies only (tier 1 or more). */
export const activeSynergies = (team: readonly { species: string }[], opts: SynergyOpts = {}) => synergies(team, opts).filter((s) => s.tier > 0);

/** The featured region's points under a scale (SYNERGY.featured; the chips' hover and the ranked theme). */
export const featuredPct = (perk: RegionPerk, scale?: SynergyScale) => scaled(SYNERGY.featured[perk], scale?.[perk]);

/** What synergies do to one creature: multipliers on HP, Speed and all damage, and on each element's moves. */
export interface SynergyMod { hp: number; swift: number; dmg: number; el: Partial<Record<Element, number>> }
const NONE = (): SynergyMod => ({ hp: 1, swift: 1, dmg: 1, el: {} });

/** Each creature's mod, by line-up index (a duplicate species gets none). Bonuses of different synergies multiply. */
export function synergyMods(team: readonly { species: string }[], opts: SynergyOpts = {}): SynergyMod[] {
  const mods = team.map(NONE);
  const keep = (s: Synergy) => !opts.only || opts.only.includes(s.kind) || opts.only.includes(`${s.kind}:${s.key}`);
  for (const s of activeSynergies(team, opts).filter(keep)) {
    const m = 1 + s.pct / 100;
    for (const i of s.who) {
      if (s.perk === 'moves') mods[i].el[s.key as Element] = (mods[i].el[s.key as Element] ?? 1) * m;
      else mods[i][s.perk] *= m;
    }
  }
  return mods;
}

/** A fighter with its synergy mod: HP and Speed on the stats, damage on Fighter.synergy (shared/combat.ts blowBase). */
export function withSynergy(f: Fighter, m: SynergyMod): Fighter {
  const plain = m.hp === 1 && m.swift === 1 && m.dmg === 1 && !Object.keys(m.el).length;
  if (plain) return f;
  return {
    ...f,
    stats: { ...f.stats, hp: f.stats.hp * m.hp, swift: f.stats.swift * m.swift },
    ...(m.dmg !== 1 || Object.keys(m.el).length ? { synergy: { dmg: m.dmg, el: m.el } } : {}),
  };
}

// ---------- words (chips and help) ----------
const REGION_NAME: Record<RegionId, string> = { midgard: 'Midgard', hellas: 'Hellas', yomi: 'Yomi' };
const PERK_NAME: Record<RegionPerk, string> = { hp: 'HP', dmg: 'Damage', swift: 'Speed' };

/** A synergy's name: "Yomi", "Tide", or "Kin" with the line's last form ("Fenrisborn kin"). */
export function synergyName(s: Pick<Synergy, 'kind' | 'key'>): string {
  if (s.kind === 'region') return REGION_NAME[s.key as RegionId] ?? s.key;
  if (s.kind === 'element') return ELEMENTS[s.key as Element]?.name ?? s.key;
  const line = LINES.find((l) => l.id === s.key);
  return `${line ? line.stages[line.stages.length - 1].name : s.key} kin`;
}

/** What it raises, in a word or two: "Speed", "HP", "Damage", "Tide moves". */
export function perkName(s: Pick<Synergy, 'kind' | 'key' | 'perk'>): string {
  return s.perk === 'moves' ? `${ELEMENTS[s.key as Element]?.name ?? s.key} moves` : PERK_NAME[s.perk];
}

/** The short label on a chip: "Yomi 2/4 · Speed +6%", "Tide 3/3 · +6%", "Fenrisborn kin · HP +3%"; a near-miss has no bonus. */
export function synergyLabel(s: Synergy): string {
  if (s.kind === 'kin') return `${synergyName(s)} · HP +${s.pct}%`;
  const count = `${synergyName(s)} ${s.count >= s.max ? s.max : s.count}/${s.tier ? s.max : s.next}`;
  if (!s.tier) return count;
  return s.kind === 'region' ? `${count} · ${perkName(s)} +${s.pct}%` : `${count} · +${s.pct}%`;
}

/**
 * The exact numbers, for the chip's hover: "Midgard creatures get HP +3% with 2 in the line-up, +5% with 4. Here:
 * Fenrisborn, Mara." `names`: the creatures' names by line-up index.
 */
export function synergyTip(s: Synergy, names: (i: number) => string = () => '', scale?: SynergyScale): string {
  const who = s.who.map(names).filter(Boolean).join(', ');
  const here = who ? ` Here: ${who}.` : '';
  if (s.kind === 'kin') return `Two forms of one line: HP +${s.pct}% each.${here}`;
  const nm = synergyName(s);
  const at = (s.kind === 'region' ? SYNERGY.regionAt : SYNERGY.elementAt) as readonly number[];
  const gets = s.kind === 'region' ? `${nm} creatures get ${perkName(s)}` : `${nm} creatures' ${nm} moves deal`;
  const feat = s.kind === 'region' && s.featured ? ` This season's featured region: +${featuredPct(s.perk as RegionPerk, scale)}% included.` : '';
  return `${gets} +${s.tiers[0]}% with ${at[0]} in the line-up, +${s.tiers[1]}% with ${at[1]}.${feat}${here}`;
}
